import React, { useCallback, useRef } from "react"
import {
  Control,
  Controller,
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
  useWatch,
} from "react-hook-form"
import { Lock } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FormErrorMessage } from "@/components/ui/form"
import { useLanguage } from "@/hooks/useLanguage"
import { usePostalCodeAutofill } from "@/hooks/usePostalCodeAutofill"

interface AddressFieldsProps {
  register: UseFormRegister<any>
  errors: FieldErrors<any>
  control: Control<any>
  setValue: UseFormSetValue<any>
  prefix?: string
}

export const AddressFields: React.FC<AddressFieldsProps> = ({
  register,
  errors,
  control,
  setValue,
  prefix = "",
}) => {
  const { t } = useLanguage()
  const text = t.savedAddresses

  const getFieldName = (field: string) => (prefix ? `${prefix}.${field}` : field)
  const getFieldError = (field: string) => {
    if (!prefix) return errors[field]
    const prefixParts = prefix.split(".")
    let currentError: any = errors
    for (const part of prefixParts) {
      if (!currentError) return undefined
      currentError = currentError[part]
    }
    return currentError?.[field]
  }

  // CP drives everything: State/City are derived + read-only, Colonia is a dropdown
  // of the CP's valid colonias. Keep the latest colonia value in a ref so a new CP
  // resolution can clear a colonia that no longer belongs to the code.
  const zipValue = useWatch({ control, name: getFieldName("zipCode") }) as string | undefined
  const coloniaValue = useWatch({ control, name: getFieldName("neighborhood") }) as string | undefined
  const coloniaValueRef = useRef<string | undefined>(undefined)
  coloniaValueRef.current = coloniaValue

  const { colonias, status } = usePostalCodeAutofill(
    zipValue,
    useCallback(
      (info) => {
        setValue(getFieldName("state"), info.state, { shouldValidate: true, shouldDirty: true })
        setValue(getFieldName("city"), info.city, { shouldValidate: true, shouldDirty: true })
        const current = coloniaValueRef.current
        if (current && !info.colonias.includes(current)) {
          setValue(getFieldName("neighborhood"), "", { shouldDirty: true })
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setValue, prefix]
    )
  )

  const readOnlyClass =
    "cursor-not-allowed bg-muted text-muted-foreground focus-visible:ring-0"

  const autoBadge = (
    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      <Lock className="h-3 w-3" />
      {text.autoFilled}
    </span>
  )

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label htmlFor={getFieldName("zipCode")}>{text.zipCode}</Label>
        <Input id={getFieldName("zipCode")} inputMode="numeric" maxLength={5} {...register(getFieldName("zipCode"))} />
        {status === "loading" && (
          <p className="text-xs text-muted-foreground">{text.cpLoading}</p>
        )}
        {status === "notfound" && <p className="text-xs text-destructive">{text.cpNotFound}</p>}
        <FormErrorMessage>{getFieldError("zipCode")?.message as string}</FormErrorMessage>
      </div>

      <div className="space-y-2">
        <Label htmlFor={getFieldName("street")}>{text.street}</Label>
        <Input id={getFieldName("street")} {...register(getFieldName("street"))} />
        <FormErrorMessage>{getFieldError("street")?.message as string}</FormErrorMessage>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor={getFieldName("externalNumber")}>{text.extNum}</Label>
          <Input id={getFieldName("externalNumber")} {...register(getFieldName("externalNumber"))} />
          <FormErrorMessage>{getFieldError("externalNumber")?.message as string}</FormErrorMessage>
        </div>
        <div className="space-y-2">
          <Label htmlFor={getFieldName("internalNumber")}>{text.intNum}</Label>
          <Input id={getFieldName("internalNumber")} {...register(getFieldName("internalNumber"))} />
          <FormErrorMessage>{getFieldError("internalNumber")?.message as string}</FormErrorMessage>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor={getFieldName("neighborhood")}>{text.neighborhood}</Label>
        <Controller
          control={control}
          name={getFieldName("neighborhood")}
          render={({ field }) => (
            <Select
              value={field.value || ""}
              onValueChange={field.onChange}
              disabled={colonias.length === 0}
            >
              <SelectTrigger id={getFieldName("neighborhood")}>
                <SelectValue
                  placeholder={colonias.length === 0 ? text.coloniaNeedsCp : text.selectColonia}
                />
              </SelectTrigger>
              <SelectContent>
                {colonias.map((colonia) => (
                  <SelectItem key={colonia} value={colonia}>
                    {colonia}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        <FormErrorMessage>{getFieldError("neighborhood")?.message as string}</FormErrorMessage>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={getFieldName("city")}>{text.city}</Label>
            {autoBadge}
          </div>
          <Input
            id={getFieldName("city")}
            readOnly
            tabIndex={-1}
            placeholder={text.cityPlaceholder}
            className={readOnlyClass}
            {...register(getFieldName("city"))}
          />
          <FormErrorMessage>{getFieldError("city")?.message as string}</FormErrorMessage>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor={getFieldName("state")}>{text.state}</Label>
            {autoBadge}
          </div>
          <Input
            id={getFieldName("state")}
            readOnly
            tabIndex={-1}
            placeholder={text.statePlaceholder}
            className={readOnlyClass}
            {...register(getFieldName("state"))}
          />
          <FormErrorMessage>{getFieldError("state")?.message as string}</FormErrorMessage>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{text.autoFilledHint}</p>
    </div>
  )
}
